import { hasConsent } from './consent';

/**
 * Signup attribution shared with the dashboard.
 *
 * One first-party cookie, scoped to `.bunny.net` so `dash.bunny.net` can read
 * it at registration. It is a Statistics cookie: the landing touch is held in
 * memory until the visitor grants Statistics, and the cookie is removed again
 * if they withdraw it.
 *
 * Cookie contract (bump `v` on breaking changes - the dashboard reads this):
 *   { v: 1, first_touch: Touch, last_touch: Touch }
 */
export const ATTRIBUTION_COOKIE_NAME = 'bunny_attribution';
export const ATTRIBUTION_COOKIE_VERSION = 1 as const;

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
const MAX_FIELD_LENGTH = 200;
const ROOT_DOMAIN = 'bunny.net';

export type Touch = {
  source: string;
  medium: string;
  campaign: string;
  content: string;
  term: string;
  referrer_domain: string;
  landing_page: string;
  timestamp: string;
};

export type Attribution = {
  v: typeof ATTRIBUTION_COOKIE_VERSION;
  first_touch: Touch;
  last_touch: Touch;
};

/**
 * Referrers (and `utm_source` values) worth grouping under one readable name.
 * Anything not listed is kept as its hostname.
 */
const SOURCE_ALIASES: Record<string, string> = {
  'chatgpt.com': 'chatgpt',
  'chat.openai.com': 'chatgpt',
  'perplexity.ai': 'perplexity',
  'claude.ai': 'claude',
  'gemini.google.com': 'gemini',
  'copilot.microsoft.com': 'copilot',
  'bing.com': 'bing',
  'duckduckgo.com': 'duckduckgo',
};

const GOOGLE_SEARCH_HOST = /^google\.[a-z.]+$/;

const truncate = (value: string) => value.slice(0, MAX_FIELD_LENGTH);

const isBunnyHost = (hostname: string) =>
  hostname === ROOT_DOMAIN || hostname.endsWith(`.${ROOT_DOMAIN}`);

export const normalizeSource = (value: string): string => {
  const host = value
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
  if (!host) return '';
  if (GOOGLE_SEARCH_HOST.test(host)) return 'google';
  return SOURCE_ALIASES[host] ?? host;
};

/**
 * `.bunny.net` on any bunny.net host so the dashboard can read it; host-only
 * everywhere else (localhost, previews) where that Domain would be rejected.
 */
export const getCookieDomain = (): string | null =>
  isBunnyHost(window.location.hostname) ? `.${ROOT_DOMAIN}` : null;

const writeRawCookie = (
  value: string,
  maxAge: number,
  domain: string | null
) => {
  document.cookie = [
    `${ATTRIBUTION_COOKIE_NAME}=${value}`,
    domain ? `Domain=${domain}` : '',
    'Path=/',
    `Max-Age=${maxAge}`,
    'SameSite=Lax',
    window.location.protocol === 'https:' ? 'Secure' : '',
  ]
    .filter(Boolean)
    .join('; ');
};

const isTouch = (value: unknown): value is Touch =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Touch).source === 'string';

export const readAttribution = (): Attribution | null => {
  if (typeof document === 'undefined') return null;
  const prefix = `${ATTRIBUTION_COOKIE_NAME}=`;
  const raw = document.cookie
    .split('; ')
    .find(row => row.startsWith(prefix))
    ?.slice(prefix.length);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<Attribution>;
    if (isTouch(parsed.first_touch) && isTouch(parsed.last_touch)) {
      return {
        v: ATTRIBUTION_COOKIE_VERSION,
        first_touch: parsed.first_touch,
        last_touch: parsed.last_touch,
      };
    }
  } catch {
    // Malformed cookie - treat as absent and let the next write replace it.
  }
  return null;
};

const writeAttribution = (attribution: Attribution) =>
  writeRawCookie(
    encodeURIComponent(JSON.stringify(attribution)),
    COOKIE_MAX_AGE_SECONDS,
    getCookieDomain()
  );

/** Clears both the shared and any host-only copy. */
export const deleteAttributionCookie = () => {
  if (typeof document === 'undefined') return;
  const domain = getCookieDomain();
  if (domain) writeRawCookie('', 0, domain);
  writeRawCookie('', 0, null);
};

const getExternalReferrerHost = (): string => {
  if (!document.referrer) return '';
  try {
    const host = new URL(document.referrer).hostname.toLowerCase();
    return isBunnyHost(host) ? '' : host;
  } catch {
    return '';
  }
};

/**
 * Describe the current arrival. `withReferrer` is false for client-side route
 * changes: `document.referrer` still holds the original landing referrer there,
 * which is not where this navigation came from.
 */
export const buildTouch = (withReferrer = true): Touch => {
  const params = new URLSearchParams(window.location.search);
  const param = (name: string) => truncate(params.get(name)?.trim() ?? '');
  const referrerHost = withReferrer ? getExternalReferrerHost() : '';

  return {
    source: truncate(
      normalizeSource(param('utm_source')) ||
        normalizeSource(referrerHost) ||
        'direct'
    ),
    medium: param('utm_medium'),
    campaign: param('utm_campaign'),
    content: param('utm_content'),
    term: param('utm_term'),
    referrer_domain: truncate(referrerHost),
    landing_page: truncate(window.location.pathname),
    timestamp: new Date().toISOString(),
  };
};

const hasCampaign = () =>
  Boolean(new URLSearchParams(window.location.search).get('utm_source'));

/**
 * A touch that may overwrite `last_touch`: it came with a `utm_source` or from
 * an external referrer. `source` is `direct` exactly when it had neither.
 */
const isQualified = (touch: Touch) => touch.source !== 'direct';

/**
 * The touch waiting for Statistics consent, held in memory only. Taken from
 * the real arrival, so accepting the banner a few pages later still records
 * where the visitor came from rather than the page they accepted on.
 */
let pendingTouch: Touch | null = null;
let landingEvaluated = false;

/**
 * Call on every route. Memory only - writes nothing, so it is safe before the
 * visitor answers. Gatsby runs `onRouteUpdate` on the initial load too; only
 * that first call looks at the referrer, and later routes count only when they
 * carry their own `utm_source`.
 */
export const rememberAttributionTouch = () => {
  if (typeof window === 'undefined') return;

  if (!landingEvaluated) {
    landingEvaluated = true;
    pendingTouch = buildTouch();
    return;
  }

  if (hasCampaign()) {
    pendingTouch = buildTouch(false);
  }
};

/** Persist the pending touch. Only call once Statistics consent is granted. */
export const persistAttribution = () => {
  if (typeof window === 'undefined' || !pendingTouch) return;
  const touch = pendingTouch;
  pendingTouch = null;

  const existing = readAttribution();
  if (!existing) {
    writeAttribution({
      v: ATTRIBUTION_COOKIE_VERSION,
      first_touch: touch,
      last_touch: touch,
    });
    return;
  }

  if (isQualified(touch)) {
    writeAttribution({
      v: ATTRIBUTION_COOKIE_VERSION,
      first_touch: existing.first_touch,
      last_touch: touch,
    });
  }
};

const handleConsentChange = () => {
  if (hasConsent('statistics')) return;
  pendingTouch = null;
  deleteAttributionCookie();
};

let bound = false;

/**
 * Remove the cookie when Statistics is withdrawn, or when a returning visitor's
 * stored answer turns out not to include it. Safe to call more than once.
 */
export const bindAttributionConsent = () => {
  if (bound || typeof window === 'undefined') return;
  bound = true;
  window.addEventListener('CookiebotOnDecline', handleConsentChange);
  window.addEventListener('CookiebotOnConsentReady', handleConsentChange);
};
